import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Input,
  Button,
  useToast,
} from "@swordsweeper/ssi-ui-component-library";
import { useFormHandlers } from "@swordsweeper/react-forms";
import * as Yup from "yup";
import AuthPageLayout from "@src/components/widgets/AuthPageLayout";
import * as SignUpApi from "@src/api/SignUpApi";
import styles from "./ClinicSetupPage.module.scss";

const phoneRegex =
  /^\(?([0-9]{3})\)?\s*[-.]?\s*([0-9]{3})\s*[-.]?\s*([0-9]{4})$/;

const schema = Yup.object().shape({
  clinicName: Yup.string().trim().required("Clinic name is required."),
  address1: Yup.string().trim().required("Street address is required."),
  address2: Yup.string().trim().nullable(),
  city: Yup.string().trim().required("City is required."),
  state: Yup.string().trim().required("State is required."),
  postalCode: Yup.string().trim().required("Postal code is required."),
  phone: Yup.string()
    .trim()
    .required("Clinic phone number is required.")
    .test(
      "phone-format",
      "Phone number must be 10 digits.",
      (v) => !v || phoneRegex.test(v),
    ),
});

interface FormData {
  clinicName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
}

type FieldName = keyof FormData;

const INITIAL_DATA: FormData = {
  clinicName: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  postalCode: "",
  phone: "",
};

const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export default function ClinicSetupPage(): React.JSX.Element {
  const navigate = useNavigate();
  const showToast = useToast();
  const { formData, handleUpdate, isValid } = useFormHandlers<FormData>(
    INITIAL_DATA,
    schema,
  );
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateField = async (name: FieldName) => {
    try {
      await schema.validateAt(name, formData);
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setErrors((prev) => ({ ...prev, [name]: err.message }));
      }
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await SignUpApi.createOrMatchClinic(formData);
      navigate("/clinic-pending");
    } catch (error: any) {
      const body = error?.response?.body;
      const fieldErrors = body && typeof body === "object" ? body : null;

      if (fieldErrors) {
        const next: Partial<Record<FieldName, string>> = {};
        for (const key of Object.keys(fieldErrors) as FieldName[]) {
          const value = (fieldErrors as Record<string, unknown>)[key];
          next[key] = Array.isArray(value) ? String(value[0]) : String(value);
        }
        if (Object.keys(next).length > 0) {
          setErrors(next);
          return;
        }
      }

      const message =
        body?.detail ||
        error?.message ||
        "Something went wrong setting up your clinic. Please try again.";
      showToast({ message, variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageLayout>
      <div className={styles.intro}>
        <h1 className={styles.title}>
          Welcome to VetGetRX! Thanks for verifying your email address.
        </h1>
        <p className={styles.subtitle}>
          For security, we verify each clinic before activating your account.
          Once validated, we&rsquo;ll let you know.
        </p>
      </div>

      <div className={styles.formCard}>
        <div className={styles.fieldStack}>
          <Field error={errors.clinicName}>
            <Input
              label="Clinic Name"
              defaultValue=""
              value={formData.clinicName}
              onChange={(value: string) =>
                handleUpdate({ name: "clinicName", value })
              }
              onBlur={() => validateField("clinicName")}
              error={!!errors.clinicName}
              width="100%"
              required
              data-testid="clinic-setup-name"
            />
          </Field>

          <Field error={errors.address1}>
            <Input
              label="Clinic Address 01"
              defaultValue=""
              value={formData.address1}
              onChange={(value: string) =>
                handleUpdate({ name: "address1", value })
              }
              onBlur={() => validateField("address1")}
              error={!!errors.address1}
              width="100%"
              required
              data-testid="clinic-setup-address-1"
            />
          </Field>

          <Field error={errors.address2}>
            <Input
              label="Clinic Address 02"
              defaultValue=""
              value={formData.address2}
              onChange={(value: string) =>
                handleUpdate({ name: "address2", value })
              }
              onBlur={() => validateField("address2")}
              error={!!errors.address2}
              width="100%"
              data-testid="clinic-setup-address-2"
            />
          </Field>

          <Field error={errors.city}>
            <Input
              label="City"
              defaultValue=""
              value={formData.city}
              onChange={(value: string) =>
                handleUpdate({ name: "city", value })
              }
              onBlur={() => validateField("city")}
              error={!!errors.city}
              width="100%"
              required
              data-testid="clinic-setup-city"
            />
          </Field>

          <div className={styles.row}>
            <Field error={errors.state}>
              <Input
                label="State"
                defaultValue=""
                value={formData.state}
                onChange={(value: string) =>
                  handleUpdate({ name: "state", value })
                }
                onBlur={() => validateField("state")}
                error={!!errors.state}
                width="100%"
                required
                data-testid="clinic-setup-state"
              />
            </Field>

            <Field error={errors.postalCode}>
              <Input
                label="Postal Code"
                defaultValue=""
                value={formData.postalCode}
                onChange={(value: string) =>
                  handleUpdate({ name: "postalCode", value })
                }
                onBlur={() => validateField("postalCode")}
                error={!!errors.postalCode}
                width="100%"
                required
                data-testid="clinic-setup-postal-code"
              />
            </Field>
          </div>

          <Field error={errors.phone}>
            <Input
              label="Clinic Phone Number"
              defaultValue=""
              type="tel"
              inputMode="numeric"
              value={formatPhone(formData.phone)}
              onChange={(value: string) =>
                handleUpdate({
                  name: "phone",
                  value: value.replace(/\D/g, "").slice(0, 10),
                })
              }
              onBlur={() => validateField("phone")}
              error={!!errors.phone}
              width="100%"
              required
              data-testid="clinic-setup-phone"
            />
          </Field>
        </div>

        <Button
          className={`btn btn-primary btn-md ${styles.submitButton}`}
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
          data-testid="clinic-setup-submit"
        >
          {isSubmitting
            ? "Creating Clinic Account..."
            : "Create Clinic Account"}
        </Button>
      </div>
    </AuthPageLayout>
  );
}

interface FieldProps {
  error?: string;
  children: React.ReactNode;
}

function Field({ error, children }: FieldProps): React.JSX.Element {
  return (
    <div className={styles.field}>
      {children}
      {error && (
        <p className={styles.errorMessage}>
          <i className="fa-light fa-circle-exclamation" aria-hidden />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}
